import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import LoadingIndicator from 'primitives/LoadingIndicator';

import ErrorMessage from 'components/ErrorMessage';

import documentsIcon from 'media/icons/documents.svg';
import informationIcon from 'media/icons/information.svg';
import kycIcon from 'media/icons/kyc.svg';

const UNIT_TRANSFER_HEADER_QUERY = gql(`
  query getUnitTransferRequestHeader($id: ID!) {
    unitTransferRequest(id: $id) {
      id
      status
      transferor { name }
      transferee { name }
    }
  }
`);

const UnitTransferPage: FC = () => {
  const navigate = useNavigate();
  const { unitTransferId } = useParams<{ unitTransferId: string }>() as { unitTransferId: string };

  const { loading, error, data, refetch } = useQuery(UNIT_TRANSFER_HEADER_QUERY, {
    variables: { id: unitTransferId },
  });

  const renderContent = () => {
    if (loading && !data) return <LoadingIndicator />;
    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const unitTransferRequest = data.unitTransferRequest;

    const tabs = [
      {
        key: 'overview',
        label: 'Overview',
        description: 'Transfer request overview',
        icon: informationIcon,
        route: `/unit-transfers/${unitTransferId}`,
        exact: true,
      },
      {
        key: 'documents',
        label: 'Documents',
        description: 'Document checklist and AI checks',
        icon: documentsIcon,
        route: `/unit-transfers/${unitTransferId}/documents`,
      },
      {
        key: 'signatures',
        label: 'Signatures',
        description: 'Signing plan and signature requests',
        icon: kycIcon,
        route: `/unit-transfers/${unitTransferId}/signatures`,
      },
    ];

    return (
      <>
        <SidebarLayoutNavigation
          title={`${unitTransferRequest.transferor.name} → ${unitTransferRequest.transferee.name}`}
          subTitle="Unit Transfer Request"
          onBackButtonClick={() => navigate(-1)}
        />
        <SidebarLayoutContent secondaryTabs={tabs}>
          <Outlet />
        </SidebarLayoutContent>
      </>
    );
  };

  return <SidebarLayout>{renderContent()}</SidebarLayout>;
};

export default UnitTransferPage;
